import { useCallback, useRef, useState } from "react";

export type ElementDimensions = {
  width: number;
  height: number;
};

export const useElementDimensions = (
  onDimensionChange?: (dimensions: ElementDimensions) => void
) => {
  const [dimensions, setDimensions] = useState<ElementDimensions>({
    width: 0,
    height: 0,
  });

  const observerRef = useRef<ResizeObserver | null>(null);
  const measureTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(onDimensionChange);

  callbackRef.current = onDimensionChange;

  const measure = useCallback(
    (node: Element) => {
      if (!node) return;

      if (measureTimeoutRef.current) {
        clearTimeout(measureTimeoutRef.current);
      }

      measureTimeoutRef.current = setTimeout(() => {
        const rect = node.getBoundingClientRect();
        const newDims = { width: rect.width, height: rect.height };

        if (
          dimensions.width !== newDims.width ||
          dimensions.height !== newDims.height
        ) {
          setDimensions(newDims);
          if (callbackRef.current) {
            callbackRef.current(newDims);
          }
        }

        measureTimeoutRef.current = null;
      }, 100);
    },
    [dimensions]
  );

  const customRef = useCallback(
    (node: Element | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (measureTimeoutRef.current) {
        clearTimeout(measureTimeoutRef.current);
        measureTimeoutRef.current = null;
      }

      if (node) {
        measureTimeoutRef.current = setTimeout(() => {
          measure(node);
        }, 100);

        const observer = new ResizeObserver(() => {
          requestAnimationFrame(() => {
            measure(node);
          });
        });

        observer.observe(node);
        observerRef.current = observer;
      }
    },
    [measure]
  );

  return [customRef, dimensions] as const;
};
